import {
  buildTicketCsvHeaders,
  transformFromTicketToCSV,
} from "../lib/transform/transformFromTicketToCsv.ts";
import { createCSVFile, downloadZip } from "../lib/exportCsv.ts";
import { createZip } from "../lib/createZipFile.ts";
import { IAttachmentFile } from "../models/attachmentFile.ts";
import { proxyHttpRequest } from "../lib/api/proxyHttpRequest.ts";
import { getCurrentDateTime } from "../lib/getCurrentDateTime.ts";
import { CancelError, CancelManager } from "../lib/cancelManager.ts";
import { ExportProgressStatus } from "../modules/parts/ExportProgressModal.tsx";
import { getPaginatedData } from "../lib/api/getPaginatedData.ts";
import { IComment, ITicket, ITicketWithRelations } from "../models/tickets.ts";
import { zendeskHttpRequest } from "../lib/api/zendeskHttpRequest.ts";
import { IUser } from "../models/user.ts";
import { prepareCsvWithHeader } from "../lib/prepareCsvWithHeader.ts";
import { extractMarkdownInlineImageInfo, IInlineImageInfo } from "../lib/extractInlineImages.ts";
import { ITICKETCSVFormat } from "../models/ticket_csv_format.ts";
import { ITicketField } from "../models/ticket_fields.ts";
import { ICustomerField } from "../models/customer.ts";
import { config, CsvEncoding } from "../lib/config.ts";

// ticket_fields.json にはシステムフィールドも含まれるため、カスタムフィールドのみを残すために除外する type 一覧
const SYSTEM_TICKET_FIELD_TYPES = new Set([
  "subject",
  "description",
  "status",
  "tickettype",
  "priority",
  "group",
  "assignee",
  "custom_status",
]);

interface TicketExportFieldOptions {
  activeTicketFields: ITicketField[];
  activeUserFields: ICustomerField[];
}

interface SkippedItem {
  type: "インライン画像" | "添付ファイル" | "コメント取得" | "ユーザー情報";
  ticketId: number;
  filePath: string;
  url: string;
  error: string;
}

const CSV_HEADER_FIELDS: { key: keyof ITICKETCSVFormat; label: string }[] = [
  { key: "created_at", label: "問合せ日時" },
  { key: "priority_products", label: "重点製品" },
  { key: "requester_company", label: "顧客会社名" },
  { key: "requester_department", label: "顧客部署名" },
  { key: "target_product", label: "お問合せ製品" },
  { key: "inquiry_type", label: "問合せ種別" },
  { key: "subject", label: "お問合せ概要（件名）" },
  { key: "description", label: "お問合せ内容" },
  { key: "comments", label: "コメント" },
  { key: "requester_name", label: "顧客名" },
  { key: "requester_email", label: "顧客メールアドレス" },
  { key: "requester_phone", label: "顧客電話番号" },
  { key: "requester_postcode", label: "顧客郵便番号" },
  { key: "requester_address", label: "顧客住所" },
  { key: "requester_industry", label: "顧客業種" },
  { key: "customer_phase", label: "顧客フェーズ" },
  { key: "id", label: "id" },
  { key: "assignee_name", label: "担当者" },
];

/**
 * 検索クエリから日付範囲を抽出し、月単位の期間リストを生成する
 */
const splitQueryByMonth = (
  query: string,
): { periods: { start: string; end: string; label: string }[]; baseQuery: string } => {
  const startMatch = query.match(/created>=(\d{4}-\d{2}-\d{2})/);
  const endMatch = query.match(/created<=(\d{4}-\d{2}-\d{2})/);

  if (!startMatch || !endMatch) {
    return { periods: [], baseQuery: query };
  }

  const startDate = startMatch[1];
  const endDate = endMatch[1];
  // 日付条件を除いたベースクエリ
  const baseQuery = query
    .replace(/created>=\d{4}-\d{2}-\d{2}\s*/g, "")
    .replace(/created<=\d{4}-\d{2}-\d{2}\s*/g, "")
    .trim();

  const periods: { start: string; end: string; label: string }[] = [];
  let current = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");

  while (current <= end) {
    const year = current.getFullYear();
    const month = current.getMonth();

    const periodStart =
      current.getTime() === new Date(startDate + "T00:00:00").getTime()
        ? startDate
        : `${year}-${String(month + 1).padStart(2, "0")}-01`;

    // 月末日を算出
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const periodEnd =
      lastDayOfMonth > end
        ? endDate
        : `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDayOfMonth.getDate()).padStart(2, "0")}`;

    const label = `${year}年${month + 1}月`;
    periods.push({ start: periodStart, end: periodEnd, label });

    // 翌月1日に進む
    current = new Date(year, month + 1, 1);
  }

  return { periods, baseQuery };
};

/**
 * 1期間分のチケットをエクスポートしてZIPをダウンロードする
 */
const exportSinglePeriod = async (
  brandUrl: string,
  token: string,
  query: string,
  periodLabel: string,
  fieldOptions: TicketExportFieldOptions,
  onUpdateTotalCount: (count: number) => void,
  onUpdateDownloadedCount: (count: number) => void,
  onUpdateInfo: (status: ExportProgressStatus, message: string) => void,
  csvEncoding: CsvEncoding,
): Promise<{ ticketCount: number; skippedCount: number }> => {
  const cancelManager = CancelManager.getInstance();

  onUpdateInfo("Export", `[${periodLabel}] チケットをダウンロードしています。(1/6)`);
  const apiUrl = `https://${import.meta.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com/api/v2/search/export?filter[type]=ticket&query=${encodeURIComponent(query)}`;
  const tickets = await getPaginatedData<ITicket>(
    apiUrl,
    token,
    brandUrl,
    "results",
  );

  if (tickets.length === 0) {
    return { ticketCount: 0, skippedCount: 0 };
  }

  const skippedItems: SkippedItem[] = [];

  cancelManager.checkCancelled();
  const ticketWithRelations = await getTicketWithRelations(
    tickets,
    token,
    brandUrl,
    onUpdateTotalCount,
    onUpdateDownloadedCount,
    (status, msg) => onUpdateInfo(status, `[${periodLabel}] ${msg}`),
    skippedItems,
  );

  onUpdateInfo("Export", `[${periodLabel}] チケットのインライン画像を処理しています。(4/6)`);
  const { modifiedTicketWithRelations, inlineImageFiles } =
    await extractAndDownloadTicketInlineImages(
      ticketWithRelations,
      token,
      onUpdateTotalCount,
      onUpdateDownloadedCount,
      skippedItems,
    );

  onUpdateInfo("Export", `[${periodLabel}] チケットの添付ファイルをダウンロードしています。(5/6)`);
  const attachments = await getAttachments(
    modifiedTicketWithRelations,
    token,
    onUpdateTotalCount,
    onUpdateDownloadedCount,
    skippedItems,
  );

  cancelManager.checkCancelled();

  onUpdateInfo("Export", `[${periodLabel}] Zipファイルを作成しています。(6/6)`);

  const allFiles: IAttachmentFile[] = [...attachments, ...inlineImageFiles];

  if (skippedItems.length > 0) {
    const report = createSkipReport(skippedItems);
    const encoder = new TextEncoder();
    allFiles.push({
      name: "skipped_files_report.txt",
      content: encoder.encode(report),
    });
  }

  const transformOptions = {
    useFixedTicketFields: config.useFixedTicketFields,
    useFixedUserFields: config.useFixedUserFields,
    enableProductLabelCleanup: config.enableProductLabelCleanup,
    activeTicketFields: fieldOptions.activeTicketFields,
    activeUserFields: fieldOptions.activeUserFields,
  };
  const csv = modifiedTicketWithRelations.map((t) =>
    transformFromTicketToCSV(t, transformOptions),
  );

  let csvFile: Uint8Array;
  if (config.useFixedTicketFields && config.useFixedUserFields) {
    // 既存の日本語ラベルと列順を維持するため prepareCsvWithHeader 経路を使う
    const { csvFields, csvData } = prepareCsvWithHeader(csv, CSV_HEADER_FIELDS);
    csvFile = createCSVFile(csvData, csvFields, csvEncoding);
  } else {
    const csvHeaders = buildTicketCsvHeaders(transformOptions);
    csvFile = createCSVFile(csv, csvHeaders, csvEncoding);
  }
  const zip = await createZip(csvFile, "export.csv", allFiles);

  downloadZip(zip, `tickets_${periodLabel}_${getCurrentDateTime()}`);

  return { ticketCount: tickets.length, skippedCount: skippedItems.length };
};

export const exportTicketDataAndAttachments = async (
  brandUrl: string,
  token: string,
  query: string,
  onUpdateTotalCount: (count: number) => void,
  onUpdateDownloadedCount: (count: number) => void,
  onUpdateInfo: (status: ExportProgressStatus, message: string) => void,
  csvEncoding: CsvEncoding,
) => {
  const cancelManager = CancelManager.getInstance();
  cancelManager.reset();
  try {
    const fieldOptions = await fetchTicketExportFieldOptions(brandUrl, token);

    const { periods, baseQuery } = splitQueryByMonth(query);

    // 1ヶ月以内の範囲、または日付パース不可の場合は従来通り一括処理
    if (periods.length <= 1) {
      const result = await exportSinglePeriod(
        brandUrl,
        token,
        query,
        "",
        fieldOptions,
        onUpdateTotalCount,
        onUpdateDownloadedCount,
        (status, message) => onUpdateInfo(status, message.replace(/^\[\]\s*/, "")),
        csvEncoding,
      );
      if (result.ticketCount === 0) {
        onUpdateInfo("Success", "エクスポート対象のチケットはありませんでした。");
        return;
      }
      const successMessage =
        result.skippedCount > 0
          ? `${result.skippedCount}件のファイルをスキップしました。詳細はZIP内の skipped_files_report.txt を確認してください。`
          : "";
      onUpdateInfo("Success", successMessage);
      return;
    }

    // 複数月にまたがる場合は月別に分割して処理
    let totalTickets = 0;
    let totalSkipped = 0;
    let downloadedZips = 0;

    for (const [index, period] of periods.entries()) {
      cancelManager.checkCancelled();
      const monthQuery = `${baseQuery} created>=${period.start} created<=${period.end}`.trim();

      onUpdateInfo(
        "Export",
        `${period.label}を処理中（${index + 1}/${periods.length}ヶ月目）`,
      );

      const result = await exportSinglePeriod(
        brandUrl,
        token,
        monthQuery,
        period.label,
        fieldOptions,
        onUpdateTotalCount,
        onUpdateDownloadedCount,
        onUpdateInfo,
        csvEncoding,
      );

      totalTickets += result.ticketCount;
      totalSkipped += result.skippedCount;
      if (result.ticketCount > 0) downloadedZips++;
    }

    if (totalTickets === 0) {
      onUpdateInfo("Success", "エクスポート対象のチケットはありませんでした。");
      return;
    }

    const messages: string[] = [`${downloadedZips}件のZIPファイルをダウンロードしました。（合計${totalTickets}チケット）`];
    if (totalSkipped > 0) {
      messages.push(`${totalSkipped}件のファイルをスキップしました。詳細は各ZIP内の skipped_files_report.txt を確認してください。`);
    }
    onUpdateInfo("Success", messages.join(" "));
  } catch (e) {
    console.error(e);
    if (e instanceof CancelError) {
      onUpdateInfo("Cancel", "");
    } else {
      onUpdateInfo("Error", `${e}`);
    }
  }
};

const CONCURRENCY = 5;
const API_DELAY = 100; // ms

const getTicketWithRelations = async (
  tickets: ITicket[],
  token: string,
  brandUrl: string,
  onUpdateTotalCount: (count: number) => void,
  onUpdateDownloadedCount: (count: number) => void,
  onUpdateInfo: (status: ExportProgressStatus, message: string) => void,
  skippedItems: SkippedItem[],
): Promise<ITicketWithRelations[]> => {
  onUpdateInfo(
    "Export",
    "チケットの関連ユーザー情報をダウンロードしています。(2/6)",
  );
  const users = await getRequestersAndAssignees(
    tickets,
    token,
    brandUrl,
    onUpdateTotalCount,
    onUpdateDownloadedCount,
    skippedItems,
  );

  const cancelManager = CancelManager.getInstance();
  onUpdateInfo("Export", "チケットのコメントをダウンロードしています。(3/6)");
  onUpdateTotalCount(tickets.length);

  const ticketWithRelations: ITicketWithRelations[] = new Array(tickets.length);
  let completedCount = 0;

  const fetchComments = async (index: number) => {
    cancelManager.checkCancelled();

    const ticket = tickets[index];
    try {
      const commentResponse = await zendeskHttpRequest<{ comments: IComment[] }>(
        `tickets/${ticket.id}/comments.json`,
        token,
        "GET",
        brandUrl,
      );

      let comments: IComment[] = commentResponse.comments || [];

      comments = comments.filter((comment) => {
        const isVoiceService =
          comment.metadata?.system?.client === "voice_service";
        const isCallSummary = comment.plain_body
          ?.trim()
          .startsWith("コール概要");
        if (!comment.public && !(isCallSummary && isVoiceService)) {
          return false;
        }
        return true;
      });

      comments = comments.map((comment) => {
        const author = users[comment.author_id] || { name: "Unknown Author" };
        return {
          ...comment,
          author_name: author.name,
          created_at: new Date(comment.created_at).toISOString(),
          formatted_body: `${author.name} : ${comment.created_at} \n${comment.body} \n`,
        };
      });

      const requester = ticket.requester_id ? users[ticket.requester_id] : null;
      const assignee = ticket.assignee_id ? users[ticket.assignee_id] : null;
      ticketWithRelations[index] = { ticket, comments, requester, assignee };
    } catch (error) {
      skippedItems.push({
        type: "コメント取得",
        ticketId: ticket.id,
        filePath: "",
        url: `tickets/${ticket.id}/comments.json`,
        error: String(error),
      });
      console.warn(`チケット ${ticket.id} のコメント取得をスキップしました: ${error}`);

      const requester = ticket.requester_id ? users[ticket.requester_id] : null;
      const assignee = ticket.assignee_id ? users[ticket.assignee_id] : null;
      ticketWithRelations[index] = { ticket, comments: [], requester, assignee };
    }
    completedCount++;
    onUpdateDownloadedCount(completedCount);
  };

  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < tickets.length) {
      cancelManager.checkCancelled();
      const idx = nextIndex++;
      await fetchComments(idx);
      await new Promise((resolve) => setTimeout(resolve, API_DELAY));
    }
  };

  const workers = Array.from(
    { length: Math.min(CONCURRENCY, tickets.length) },
    () => worker(),
  );
  await Promise.all(workers);

  return ticketWithRelations;
};

/**
 * チケットのコメント本文からMarkdown形式のインライン画像を抽出・ダウンロードし、プレースホルダーに置換する
 */
const extractAndDownloadTicketInlineImages = async (
  ticketWithRelations: ITicketWithRelations[],
  token: string,
  onUpdateTotalCount: (count: number) => void,
  onUpdateDownloadedCount: (count: number) => void,
  skippedItems: SkippedItem[],
): Promise<{
  modifiedTicketWithRelations: ITicketWithRelations[];
  inlineImageFiles: IAttachmentFile[];
}> => {
  const cancelManager = CancelManager.getInstance();
  const inlineImageFiles: IAttachmentFile[] = [];

  // 全チケットの全コメントから画像URLを抽出
  const extractionResults: {
    ticketIndex: number;
    commentIndex: number;
    result: { modifiedBody: string; images: IInlineImageInfo[] };
  }[] = [];

  for (const [ti, twr] of ticketWithRelations.entries()) {
    let imageCounter = 1;
    for (const [ci, comment] of twr.comments.entries()) {
      const result = extractMarkdownInlineImageInfo(
        twr.ticket.id,
        comment.body,
        imageCounter,
      );
      imageCounter += result.images.length;
      if (result.images.length > 0) {
        extractionResults.push({ ticketIndex: ti, commentIndex: ci, result });
      }
    }
  }

  // チケットIDと画像情報を紐付けたリストを作成
  const allImageItems = extractionResults.flatMap((er) =>
    er.result.images.map((img) => ({
      ticketId: ticketWithRelations[er.ticketIndex].ticket.id,
      imageInfo: img,
    })),
  );
  onUpdateTotalCount(allImageItems.length);

  // 画像をダウンロード（並列処理、失敗時はスキップ）
  let downloadedCount = 0;
  for (let i = 0; i < allImageItems.length; i += CONCURRENCY) {
    cancelManager.checkCancelled();
    const batch = allImageItems.slice(i, i + CONCURRENCY);

    const results = await Promise.allSettled(
      batch.map(async (item) => {
        const response = await proxyHttpRequest<ArrayBuffer>(
          token,
          item.imageInfo.originalUrl,
          true,
        );
        return {
          ticketId: item.ticketId,
          filePath: item.imageInfo.filePath,
          url: item.imageInfo.originalUrl,
          content: new Uint8Array(response),
        };
      }),
    );

    for (const [idx, result] of results.entries()) {
      downloadedCount++;
      onUpdateDownloadedCount(downloadedCount);
      if (result.status === "fulfilled") {
        inlineImageFiles.push({
          name: result.value.filePath,
          content: result.value.content,
        });
      } else {
        const item = batch[idx];
        skippedItems.push({
          type: "インライン画像",
          ticketId: item.ticketId,
          filePath: item.imageInfo.filePath,
          url: item.imageInfo.originalUrl,
          error: String(result.reason),
        });
        console.warn(`インライン画像のダウンロードをスキップしました: ${result.reason}`);
      }
    }

    await new Promise((resolve) => setTimeout(resolve, API_DELAY));
  }

  // 置換済みbodyでチケットリストを再構築
  const modifiedTicketWithRelations = ticketWithRelations.map((twr) => ({
    ...twr,
    comments: twr.comments.map((comment) => ({ ...comment })),
  }));

  for (const er of extractionResults) {
    modifiedTicketWithRelations[er.ticketIndex].comments[
      er.commentIndex
    ].body = er.result.modifiedBody;
  }

  return { modifiedTicketWithRelations, inlineImageFiles };
};

const getAttachments = async (
  ticketWithRelations: ITicketWithRelations[],
  token: string,
  onUpdateTotalCount: (count: number) => void,
  onUpdateDownloadedCount: (count: number) => void,
  skippedItems: SkippedItem[],
): Promise<IAttachmentFile[]> => {
  const cancelManager = CancelManager.getInstance();
  onUpdateTotalCount(ticketWithRelations.length);

  // 全添付ファイルを事前に収集
  const allAttachmentItems: {
    ticketId: number;
    contentUrl: string;
    fileName: string;
  }[] = [];
  for (const ticketWithRelation of ticketWithRelations) {
    for (const comment of ticketWithRelation.comments) {
      for (const attachment of comment.attachments || []) {
        allAttachmentItems.push({
          ticketId: ticketWithRelation.ticket.id,
          contentUrl: attachment.content_url,
          fileName: attachment.file_name,
        });
      }
    }
  }

  onUpdateTotalCount(allAttachmentItems.length);
  const attachmentFiles: IAttachmentFile[] = [];
  let downloadedIdx = 0;

  for (let i = 0; i < allAttachmentItems.length; i += CONCURRENCY) {
    cancelManager.checkCancelled();
    const batch = allAttachmentItems.slice(i, i + CONCURRENCY);

    const results = await Promise.allSettled(
      batch.map(async (item) => {
        const response = await proxyHttpRequest<ArrayBuffer>(
          token,
          item.contentUrl,
          true,
        );
        return {
          name: `attachments/${item.ticketId}/${item.fileName}`,
          content: new Uint8Array(response),
        };
      }),
    );

    for (const [idx, result] of results.entries()) {
      downloadedIdx++;
      onUpdateDownloadedCount(downloadedIdx);
      if (result.status === "fulfilled") {
        attachmentFiles.push(result.value);
      } else {
        const item = batch[idx];
        skippedItems.push({
          type: "添付ファイル",
          ticketId: item.ticketId,
          filePath: `attachments/${item.ticketId}/${item.fileName}`,
          url: item.contentUrl,
          error: String(result.reason),
        });
        console.warn(`添付ファイルのダウンロードをスキップしました: ${result.reason}`);
      }
    }

    await new Promise((resolve) => setTimeout(resolve, API_DELAY));
  }

  return attachmentFiles;
};

/** users/show_many APIの1リクエストあたりの最大ID数 */
const USERS_BATCH_SIZE = 100;

const getRequestersAndAssignees = async (
  tickets: ITicket[],
  token: string,
  brandUrl: string,
  onUpdateTotalCount: (count: number) => void,
  onUpdateDownloadedCount: (count: number) => void,
  skippedItems: SkippedItem[],
): Promise<Record<number, IUser>> => {
  const cancelManager = CancelManager.getInstance();

  const userIds = new Set<number>();
  tickets.forEach((ticket) => {
    if (ticket.requester_id) {
      userIds.add(ticket.requester_id);
    }
    if (ticket.assignee_id) {
      userIds.add(ticket.assignee_id);
    }
  });

  const userIdArray = Array.from(userIds);
  const batches: number[][] = [];
  for (let i = 0; i < userIdArray.length; i += USERS_BATCH_SIZE) {
    batches.push(userIdArray.slice(i, i + USERS_BATCH_SIZE));
  }

  const userDetails: Record<number, IUser> = {};

  onUpdateTotalCount(batches.length);
  let completedCount = 0;
  for (const batch of batches) {
    cancelManager.checkCancelled();
    try {
      const response = await zendeskHttpRequest<{ users: IUser[] }>(
        `users/show_many.json?ids=${batch.join(",")}`,
        token,
        "GET",
        brandUrl,
      );
      for (const user of response.users) {
        userDetails[user.id] = user;
      }
    } catch (error) {
      skippedItems.push({
        type: "ユーザー情報",
        ticketId: 0,
        filePath: "",
        url: `users/show_many.json?ids=${batch.join(",")}`,
        error: String(error),
      });
      console.warn(`ユーザー情報の一括取得をスキップしました (${batch.length}件): ${error}`);
    }
    completedCount++;
    onUpdateDownloadedCount(completedCount);
    await new Promise((resolve) => setTimeout(resolve, API_DELAY));
  }

  return userDetails;
};

const fetchTicketExportFieldOptions = async (
  brandUrl: string,
  token: string,
): Promise<TicketExportFieldOptions> => {
  const activeTicketFields: ITicketField[] = [];
  const activeUserFields: ICustomerField[] = [];

  if (!config.useFixedTicketFields) {
    const res = await zendeskHttpRequest<{ ticket_fields: ITicketField[] }>(
      "ticket_fields.json",
      token,
      "GET",
      brandUrl,
    );
    for (const field of res.ticket_fields) {
      if (field.active === false) continue;
      if (SYSTEM_TICKET_FIELD_TYPES.has(field.type ?? "")) continue;
      activeTicketFields.push(field);
    }
  }

  if (!config.useFixedUserFields) {
    const res = await zendeskHttpRequest<{ user_fields: ICustomerField[] }>(
      "user_fields.json",
      token,
      "GET",
      brandUrl,
    );
    for (const field of res.user_fields) {
      if (field.active) activeUserFields.push(field);
    }
  }

  return { activeTicketFields, activeUserFields };
};

const createSkipReport = (skippedItems: SkippedItem[]): string => {
  const lines: string[] = [
    "=== ダウンロードスキップレポート ===",
    `生成日時: ${new Date().toLocaleString("ja-JP")}`,
    `スキップ件数: ${skippedItems.length}件`,
    "",
    "---",
    "",
  ];

  for (const [index, item] of skippedItems.entries()) {
    lines.push(
      `[${index + 1}] 種別: ${item.type}`,
      `    チケットID: ${item.ticketId}`,
      `    ファイルパス: ${item.filePath}`,
      `    URL: ${item.url}`,
      `    エラー: ${item.error}`,
      "",
    );
  }

  return lines.join("\n");
};
